import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    MenuItem,
    Pagination,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PageTitle from '../shared/PageTitle';
import PageArea from '../shared/PageArea';
import LoadingWheel from '../shared/LoadingWheel';
import RoleFormDialog, { RoleFormValues } from './RoleFormDialog';
import { RolesClient, RolePermissionDto, RoleSummaryDto } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { useTranslation } from 'react-i18next';

const pageSizeOptions = [10, 25, 50];

const RolesPage: React.FC = () => {
    const { t } = useTranslation();
    const rolesClient = useMemo(() => new RolesClient(getApiBaseUrl(), authenticatedFetch), []);
    const [roles, setRoles] = useState<RoleSummaryDto[]>([]);
    const [permissions, setPermissions] = useState<RolePermissionDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [formState, setFormState] = useState<{ open: boolean; mode: 'create' | 'edit'; roleId?: string } | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [dialogValues, setDialogValues] = useState<RoleFormValues | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingRole, setIsLoadingRole] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<RoleSummaryDto | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

    const loadRoles = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await rolesClient.getApiRoles(pageNumber, pageSize, searchTerm || undefined);
            setRoles(response.items ?? []);
            setTotalPages(Math.max(response.totalPages ?? 1, 1));
            setTotalCount(response.totalCount ?? 0);
        } catch (err) {
            const message = err instanceof Error ? err.message : t('roles.errors.load');
            setError(message);
            setRoles([]);
        } finally {
            setLoading(false);
        }
    }, [pageNumber, pageSize, searchTerm, rolesClient, t]);

    useEffect(() => {
        loadRoles();
    }, [loadRoles]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPageNumber(1);
            setSearchTerm(searchInput.trim());
        }, 400);

        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const loadPermissions = async () => {
            try {
                const data = await rolesClient.getApiRolesPermissions();
                setPermissions(data);
            } catch (err) {
                console.error('Failed to load permissions', err);
            }
        };

        loadPermissions();
    }, [rolesClient]);

    const ensurePermissionsLoaded = () => {
        if (permissions.length === 0) {
            setNotification({ severity: 'error', message: t('roles.errors.permissionsUnavailable') });
            return false;
        }

        return true;
    };

    const handleOpenCreate = () => {
        if (!ensurePermissionsLoaded()) {
            return;
        }

        setFormError(null);
        setDialogValues(null);
        setFormState({ open: true, mode: 'create' });
    };

    const handleOpenEdit = async (role: RoleSummaryDto) => {
        if (!ensurePermissionsLoaded()) {
            return;
        }

        setFormError(null);
        setDialogValues(null);
        if (!role.id) {
            setFormError(t('roles.errors.loadRole'));
            return;
        }

        setFormState({ open: true, mode: 'edit', roleId: role.id });
        setIsLoadingRole(true);

        try {
            const details = await rolesClient.getApiRoles2(role.id);
            setDialogValues({
                name: details.name ?? '',
                description: details.description ?? '',
                permissionIds: details.permissionIds ?? [],
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : t('roles.errors.loadRole');
            setFormError(message);
        } finally {
            setIsLoadingRole(false);
        }
    };

    const closeDialog = () => {
        setFormState(null);
        setFormError(null);
        setDialogValues(null);
        setIsSaving(false);
        setIsLoadingRole(false);
    };

    const handleSaveRole = async (values: RoleFormValues) => {
        if (!formState) {
            return;
        }

        setIsSaving(true);
        setFormError(null);

        try {
            if (formState.mode === 'create') {
                await rolesClient.postApiRoles({
                    name: values.name,
                    description: values.description,
                    permissionIds: values.permissionIds,
                });
                setNotification({ severity: 'success', message: t('roles.feedback.createSuccess') });
            } else if (formState.roleId) {
                await rolesClient.putApiRoles(formState.roleId, {
                    name: values.name,
                    description: values.description,
                    permissionIds: values.permissionIds,
                });
                setNotification({ severity: 'success', message: t('roles.feedback.updateSuccess') });
            }

            closeDialog();
            await loadRoles();
        } catch (err) {
            const message = err instanceof Error ? err.message : t('roles.errors.save');
            setFormError(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenDelete = (role: RoleSummaryDto) => {
        setDeleteTarget(role);
        setDeleteError(null);
    };

    const handleDeleteRole = async () => {
        if (!deleteTarget) {
            return;
        }

        setIsDeleting(true);
        setDeleteError(null);

        try {
            if (!deleteTarget.id) {
                setDeleteError(t('roles.errors.delete'));
                return;
            }

            await rolesClient.deleteApiRoles(deleteTarget.id);
            setNotification({ severity: 'success', message: t('roles.feedback.deleteSuccess') });
            setDeleteTarget(null);
            await loadRoles();
        } catch (err) {
            const message = err instanceof Error ? err.message : t('roles.errors.delete');
            setDeleteError(message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = Number(event.target.value);
        setPageSize(value);
        setPageNumber(1);
    };

    const pagedDescription = useMemo(
        () =>
            t('roles.pagination.summary', {
                count: totalCount,
            }),
        [t, totalCount]
    );

    if (loading) {
        return <LoadingWheel />;
    }

    return (
        <PageArea fullHeight>
            <PageTitle title={t('roles.title')} subtitle={t('roles.subtitle')} />
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3, gap: 2 }}
            >
                <TextField
                    placeholder={t('roles.searchPlaceholder')}
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    fullWidth
                />

                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        select
                        label={t('roles.pageSize')}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        sx={{ minWidth: 140 }}
                    >
                        {pageSizeOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        size="lg"
                        sx={{ minWidth: 200 }}
                    >
                        {t('roles.actions.add')}
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert variant='filled' severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {roles.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="textMD" color="text.secondary">
                        {t('roles.emptyState')}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('roles.table.name')}</TableCell>
                                        <TableCell>{t('roles.table.description')}</TableCell>
                                        <TableCell>{t('roles.table.permissions')}</TableCell>
                                        <TableCell align="right">{t('roles.table.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {roles.map((role) => {
                                        const permissionsList = role.permissions ?? [];
                                        const displayedPermissions = permissionsList.slice(0, 3);
                                        const totalPermissionCount = role.permissionCount ?? permissionsList.length;
                                        const hiddenPermissionCount = Math.max(totalPermissionCount - displayedPermissions.length, 0);

                                        return (
                                            <TableRow
                                                key={role.id}
                                                hover
                                                onClick={() => handleOpenEdit(role)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell variant="clampedText">
                                                    <Typography variant="textMD" sx={{ fontWeight: 600 }}>
                                                        {role.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell variant="clampedText">
                                                    <Typography variant="textSM" color="text.secondary">
                                                        {role.description || t('roles.noDescription')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell variant="clampedText">
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                        {displayedPermissions.map((permission) => (
                                                            <Chip key={permission} label={permission} size="small" variant="outlined" />
                                                        ))}
                                                        {hiddenPermissionCount > 0 && (
                                                            <Chip
                                                                label={t('roles.morePermissions', { count: hiddenPermissionCount })}
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Tooltip title={t('common.edit')}>
                                                            <Button
                                                                variant="secondaryIconOnly"
                                                                size="lg"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleOpenEdit(role);
                                                                }}
                                                            >
                                                                <EditIcon />
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title={t('common.delete')}>
                                                            <Button
                                                                variant="badIconOnly"
                                                                size="lg"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleOpenDelete(role);
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </Button>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>

                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" sx={{ mt: 3 }} spacing={2}>
                        <Pagination
                            count={totalPages}
                            page={Math.min(pageNumber, totalPages)}
                            onChange={(_, page) => setPageNumber(page)}
                            color="primary"
                        />
                        <Typography variant="textSM" color="text.secondary">
                            {pagedDescription}
                        </Typography>
                    </Stack>
                </>
            )}

            <RoleFormDialog
                open={Boolean(formState?.open)}
                title={formState?.mode === 'edit' ? t('roles.dialog.editTitle') : t('roles.dialog.createTitle')}
                permissions={permissions}
                initialValues={dialogValues ?? undefined}
                isSubmitting={isSaving}
                isLoading={formState?.mode === 'edit' && isLoadingRole}
                errorMessage={formError}
                onClose={closeDialog}
                onSubmit={handleSaveRole}
            />

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon color="warning" />
                    {t('roles.delete.title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="textMD" sx={{ mb: 2 }}>
                        {t('roles.delete.message', { name: deleteTarget?.name ?? '' })}
                    </Typography>
                    {deleteError && (
                        <Alert variant='filled' severity="error">{deleteError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="secondary" size="lg" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="bad" size="lg" onClick={handleDeleteRole} disabled={isDeleting}>
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {notification && (
                <Snackbar
                    open
                    autoHideDuration={4000}
                    onClose={() => setNotification(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity={notification.severity}
                        variant="filled"
                        onClose={() => setNotification(null)}
                        sx={{ width: '100%' }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            )}
        </PageArea>
    );
};

export default RolesPage;
